import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Archive, ArrowLeft, ArrowRightLeft, CircleCheck, EllipsisVertical, ExternalLink, MapPinOff, Pencil, Plus, Printer, ScanBarcode, Tag, TicketX, UserRound } from 'lucide-react'
import { Alerta, Badge, Boton, Cargando, Dialogo, Fab, Menu, Panel, Pestanas } from '../../components/kit'
import { BarraSeleccion } from '../../components/inventario/BarraSeleccion'
import { DialogoBaja } from '../../components/inventario/DialogoBaja'
import { DialogoBien } from '../../components/inventario/DialogoBien'
import { DialogoEncargado } from '../../components/inventario/DialogoEncargado'
import { DialogoNoUbicado } from '../../components/inventario/DialogoNoUbicado'
import { DialogoPlancheta } from '../../components/inventario/DialogoPlancheta'
import { DialogoSolicitarTraslado } from '../../components/inventario/DialogoSolicitarTraslado'
import { DialogoTraslado } from '../../components/inventario/DialogoTraslado'
import { DialogoEtiquetas } from '../../components/inventario/Etiquetas'
import { Historial } from '../../components/inventario/Historial'
import { RevisarPorNumero } from '../../components/inventario/RevisarPorNumero'
import { TablaBienes } from '../../components/inventario/TablaBienes'
import { useAuth } from '../../contexts/AuthProvider'
import { useBienesDeRecinto, useEditarBien, useHistorialRecinto, useMarcarEtiquetas, useMarcarUbicado, useRecinto } from '../../queries/useInventario'
import { Bien, DatosBien } from '../../types/inventario'
import { mensajeDeError } from '../../utils/errores'
import { formatoFecha } from '../../utils/fecha'
import { PLANCHETA, ubicacion } from '../../utils/inventario'

type DialogoAbierto =
  | { tipo: 'agregar'; inicial?: DatosBien }
  | { tipo: 'editar'; bien: Bien }
  | { tipo: 'trasladar'; bienes: Bien[] }
  | { tipo: 'solicitar'; bienes: Bien[] }
  | { tipo: 'no-ubicado'; bien: Bien }
  | { tipo: 'baja'; bienes: Bien[] }
  | { tipo: 'etiquetas'; bienes: Bien[] }
  | { tipo: 'encargado' }
  | { tipo: 'plancheta' }
  | { tipo: 'revisar' }

/**
 * La ficha de una oficina y el lugar de trabajo de la revisión: comparar lo que hay con
 * lo que dice el sistema (revisar por N°), agregar, traer, corregir o marcar bienes, y
 * luego imprimir la plancheta. Quien no administra bienes solo la consulta y pide traslados.
 */
export default function Oficina() {
  const admin = useAuth().puede('administra_bienes')
  const { codigo = '' } = useParams()
  const recinto = useRecinto(codigo)
  const bienes = useBienesDeRecinto(codigo)
  const [pestana, setPestana] = useState('bienes')
  const historial = useHistorialRecinto(codigo, pestana === 'historial')
  const [dialogo, setDialogo] = useState<DialogoAbierto | null>(null)
  const [seleccion, setSeleccion] = useState<Set<number>>(new Set())
  const [resaltados, setResaltados] = useState<Set<number>>(new Set())
  const ubicar = useMarcarUbicado()
  const editar = useEditarBien()
  const marcarEtiquetas = useMarcarEtiquetas()

  // La selección no sobrevive a un cambio de oficina ni a bienes que ya se fueron.
  useEffect(() => setSeleccion(new Set()), [codigo])
  useEffect(() => {
    const ids = new Set((bienes.data ?? []).map((b) => b.id))
    setSeleccion((s) => new Set([...s].filter((id) => ids.has(id))))
  }, [bienes.data])

  const resaltar = (ids: number[]) => {
    setPestana('bienes')
    setResaltados(new Set(ids))
    requestAnimationFrame(() => document.getElementById(`bien-${ids[0]}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }))
  }

  if (recinto.isLoading) return <Cargando />
  if (recinto.isError || !recinto.data) return <Alerta tipo="error">{mensajeDeError(recinto.error, 'No se encontró la oficina.')}</Alerta>
  const r = recinto.data
  const lista = bienes.data ?? []
  const elegidos = lista.filter((b) => seleccion.has(b.id))
  const sinEtiqueta = lista.filter((b) => b.etiqueta_pendiente && b.estado === 'ACTIVO')
  const marcarEtiqueta = (pendiente: boolean) =>
    marcarEtiquetas.mutate({ bienes: elegidos.map((b) => b.id), pendiente }, { onSuccess: () => setSeleccion(new Set()) })

  const accion = (b: Bien) => (clave: string) => {
    if (clave === 'editar') setDialogo({ tipo: 'editar', bien: b })
    else if (clave === 'trasladar') setDialogo({ tipo: 'trasladar', bienes: [b] })
    else if (clave === 'no-ubicado') setDialogo({ tipo: 'no-ubicado', bien: b })
    else if (clave === 'baja') setDialogo({ tipo: 'baja', bienes: [b] })
    else if (clave === 'ubicado') ubicar.mutate({ id: b.id, recinto: r.codigo })
    else if (clave === 'etiqueta') editar.mutate({ id: b.id, datos: { etiqueta_pendiente: false } })
    else if (clave === 'falta-etiqueta') editar.mutate({ id: b.id, datos: { etiqueta_pendiente: true } })
    else if (clave === 'imprimir') setDialogo({ tipo: 'etiquetas', bienes: [b] })
    else if (clave === 'solicitar') setDialogo({ tipo: 'solicitar', bienes: [b] })
  }

  return (
    <div className="pila pila--amplia">
      <div className="no-print">
        <Boton variante="text" tam="sm" icono={ArrowLeft} to="/app/oficinas">Oficinas</Boton>
      </div>

      <header className="pagina__cabecera pagina__cabecera--con-accion">
        <div>
          <h1 className="dcc-t-heading-m">{r.nombre}</h1>
          <p className="texto-secundario">{r.codigo} · {ubicacion(r)}</p>
        </div>
        {admin && (
          <div className="acciones-pagina">
            <Boton icono={Plus} onClick={() => setDialogo({ tipo: 'agregar' })}>Agregar bien</Boton>
            <Boton icono={Printer} variante="outlined" to={`/app/oficinas/${r.codigo}/plancheta`}>Plancheta</Boton>
          </div>
        )}
      </header>

      <Panel>
        <dl className="datos">
          <dt>Plancheta</dt>
          <dd className="datos__con-accion">
            <Badge tono={PLANCHETA[r.plancheta_estado].tono} punto>{PLANCHETA[r.plancheta_estado].texto}</Badge>
            <span className="texto-secundario">desde {formatoFecha(r.plancheta_desde)}</span>
            {admin && <Boton variante="text" tam="sm" onClick={() => setDialogo({ tipo: 'plancheta' })}>Marcar…</Boton>}
          </dd>
          <dt>Encargado</dt>
          <dd className="datos__con-accion">
            {r.encargado ? r.encargado.nombre : <span className="texto-secundario">Sin asignar</span>}
            {admin && (
              <Boton variante="text" tam="sm" icono={UserRound} onClick={() => setDialogo({ tipo: 'encargado' })}>
                {r.encargado ? 'Cambiar' : 'Asignar'}
              </Boton>
            )}
          </dd>
          <dt>Bienes</dt>
          <dd>
            {r.n_bienes} {r.n_bienes === 1 ? 'activo' : 'activos'}
            {r.n_no_ubicados > 0 && ` · ${r.n_no_ubicados} ${r.n_no_ubicados === 1 ? 'no ubicado' : 'no ubicados'}`}
          </dd>
          <dt>Etiquetas</dt>
          <dd className="datos__con-accion">
            {sinEtiqueta.length ? `${sinEtiqueta.length} ${sinEtiqueta.length === 1 ? 'pendiente' : 'pendientes'}` : 'Ninguna pendiente'}
            {admin && sinEtiqueta.length > 0 && (
              <Boton variante="text" tam="sm" icono={Printer} onClick={() => setDialogo({ tipo: 'etiquetas', bienes: sinEtiqueta })}>
                Imprimir
              </Boton>
            )}
          </dd>
        </dl>
      </Panel>

      {/* En el celular la revisión se abre con el FAB (más abajo), siempre a mano al recorrer la lista. */}
      {admin && (
        <>
          <div className="solo-escritorio">
            <RevisarPorNumero recinto={r} bienes={lista} onResaltar={resaltar} onRegistrar={(inicial) => setDialogo({ tipo: 'agregar', inicial })} />
          </div>
          <Fab icono={ScanBarcode} etiqueta="Revisar por N°" onClick={() => setDialogo({ tipo: 'revisar' })} />
        </>
      )}

      <Pestanas
        etiqueta="Contenido de la oficina"
        activa={pestana}
        onCambiar={setPestana}
        pestanas={[
          { clave: 'bienes', texto: `Bienes (${lista.length})` },
          { clave: 'historial', texto: 'Historial' },
        ]}
      >
        {pestana === 'bienes' ? (
          <div className="pila">
            {lista.length > 0 && (
              <BarraSeleccion
                texto={admin ? 'Marca varios para trasladarlos, imprimir sus etiquetas o darlos de baja juntos.' : 'Marca varios para pedir que los trasladen.'}
                marcados={elegidos.length}
                acciones={!admin ? [{ clave: 'solicitar', texto: 'Solicitar traslado', Icono: ArrowRightLeft, onClick: () => setDialogo({ tipo: 'solicitar', bienes: elegidos }) }] : [
                  { clave: 'trasladar', texto: 'Trasladar', Icono: ArrowRightLeft, onClick: () => setDialogo({ tipo: 'trasladar', bienes: elegidos }) },
                  { clave: 'imprimir', texto: 'Imprimir etiquetas', Icono: Printer, onClick: () => setDialogo({ tipo: 'etiquetas', bienes: elegidos }) },
                  { clave: 'baja', texto: 'Dar de baja', Icono: Archive, color: 'error', onClick: () => setDialogo({ tipo: 'baja', bienes: elegidos }) },
                  { clave: 'pegada', texto: 'Etiqueta pegada', Icono: Tag, secundaria: true, onClick: () => marcarEtiqueta(false) },
                  { clave: 'falta', texto: 'Falta etiqueta', Icono: TicketX, secundaria: true, onClick: () => marcarEtiqueta(true) },
                ]}
                onDesmarcar={() => setSeleccion(new Set())}
              />
            )}
            {bienes.isLoading && <Cargando />}
            {bienes.isError && <Alerta tipo="error">{mensajeDeError(bienes.error)}</Alerta>}
            {(ubicar.isError || editar.isError || marcarEtiquetas.isError) && (
              <Alerta tipo="error">{mensajeDeError(ubicar.error ?? editar.error ?? marcarEtiquetas.error)}</Alerta>
            )}
            {bienes.data && !lista.length && <p className="texto-secundario">El sistema no tiene bienes en esta oficina.</p>}
            {lista.length > 0 && (
              <TablaBienes
                bienes={lista}
                etiqueta={`Bienes de ${r.nombre}`}
                seleccion={seleccion}
                onSeleccion={setSeleccion}
                resaltados={resaltados}
                acciones={(b) => (
                  <Menu
                    etiqueta={`Acciones de ${b.numero || b.descripcion}`}
                    claseDisparador="menu-fila__disparador"
                    disparador={<EllipsisVertical className="dcc-i" size={20} aria-hidden />}
                    alineado="fin"
                    flotante
                    opciones={!admin ? [{ clave: 'solicitar', texto: 'Solicitar traslado…', Icono: ArrowRightLeft }] : [
                      { clave: 'editar', texto: 'Editar', Icono: Pencil, grupo: 0 },
                      { clave: 'trasladar', texto: 'Trasladar a…', Icono: ArrowRightLeft, grupo: 0 },
                      b.etiqueta_pendiente
                        ? { clave: 'etiqueta', texto: 'Etiqueta pegada', Icono: Tag, grupo: 1 }
                        : { clave: 'falta-etiqueta', texto: 'Falta etiqueta', Icono: TicketX, grupo: 1 },
                      { clave: 'imprimir', texto: 'Imprimir etiqueta', Icono: Printer, grupo: 1 },
                      b.estado === 'NO_UBICADO'
                        ? { clave: 'ubicado', texto: 'Está aquí', Icono: CircleCheck, grupo: 2 }
                        : { clave: 'no-ubicado', texto: 'No está aquí', Icono: MapPinOff, grupo: 2, peligro: true },
                      { clave: 'baja', texto: 'Dar de baja', Icono: Archive, grupo: 2, peligro: true },
                    ]}
                    onElegir={accion(b)}
                  />
                )}
              />
            )}
          </div>
        ) : (
          <>
            {historial.isLoading && <Cargando />}
            {historial.isError && <Alerta tipo="error">{mensajeDeError(historial.error)}</Alerta>}
            {historial.data && <Historial movimientos={historial.data} enRecinto />}
          </>
        )}
      </Pestanas>

      <p className="texto-secundario">
        <Link to={`/app/bienes?recinto=${r.codigo}`}>
          Ver en la lista general de bienes <ExternalLink className="dcc-i" size={14} aria-hidden />
        </Link>
      </p>

      {dialogo?.tipo === 'agregar' && <DialogoBien recinto={r.codigo} inicial={dialogo.inicial} onCerrar={() => setDialogo(null)} />}
      {dialogo?.tipo === 'editar' && <DialogoBien bien={dialogo.bien} onCerrar={() => setDialogo(null)} />}
      {dialogo?.tipo === 'trasladar' && (
        <DialogoTraslado bienes={dialogo.bienes} onCerrar={() => setDialogo(null)} onHecho={() => setSeleccion(new Set())} />
      )}
      {dialogo?.tipo === 'solicitar' && (
        <DialogoSolicitarTraslado bienes={dialogo.bienes} onCerrar={() => setDialogo(null)} onHecho={() => setSeleccion(new Set())} />
      )}
      {dialogo?.tipo === 'no-ubicado' && <DialogoNoUbicado bien={dialogo.bien} onCerrar={() => setDialogo(null)} />}
      {dialogo?.tipo === 'etiquetas' && <DialogoEtiquetas bienes={dialogo.bienes} onCerrar={() => setDialogo(null)} />}
      {dialogo?.tipo === 'baja' && (
        <DialogoBaja bienes={dialogo.bienes} onCerrar={() => setDialogo(null)} onHecho={() => setSeleccion(new Set())} />
      )}
      {dialogo?.tipo === 'encargado' && <DialogoEncargado recinto={r} onCerrar={() => setDialogo(null)} />}
      {dialogo?.tipo === 'plancheta' && <DialogoPlancheta recinto={r} onCerrar={() => setDialogo(null)} />}
      {dialogo?.tipo === 'revisar' && (
        // Queda abierto entre lecturas: se revisa un bien tras otro sin volver a tocar el FAB.
        <Dialogo abierto onCerrar={() => setDialogo(null)} titulo={`Revisar ${r.nombre}`}>
          <RevisarPorNumero
            recinto={r}
            bienes={lista}
            onResaltar={resaltar}
            onRegistrar={(inicial) => setDialogo({ tipo: 'agregar', inicial })}
            enDialogo
          />
        </Dialogo>
      )}
    </div>
  )
}
